import React, { FocusEventHandler, useMemo, useRef } from 'react';
import HTMLParser from 'html-react-parser';
import { v4 as uuid } from 'uuid';
import { FuelTextField } from '@frete.com/fuel-react';
import ReactDOM from 'react-dom';
import { Arrow, Container, OptionItem, OptionsContent, OptionsWrapper, OptionsWrapperCloser } from './styled';
import { Option, SelectProps } from './types';
import { useSelect } from './hooks';
import { defaultNoContentMsg, makeSelectSearch, notContentFunction, replaceSelectSearch } from './hooks/functions';

const SelectPortal: React.FC = ({ children }) => {
  const targetId = 'select-portal';
  let target = document.getElementById(targetId);

  if (!target) {
    target = document.createElement('div');
    target.id = targetId;
    document.body.appendChild(target);
  }

  return ReactDOM.createPortal(<>{children}</>, target);
};

export const Select: React.FC<SelectProps> = ({ ...props }) => {
  const {
    error,
    label,
    name,
    placeholder,
    scrollable,
    noArrow,
    noContent = notContentFunction,
    inputAutoFocus = false,
    disabled = false,
    onFocus,
    ...useSelectProps
  } = props;

  const inputRef = useRef<HTMLFuelTextFieldElement>(null);
  const optionsContentRef = useRef<HTMLDivElement>(null);
  const currentError = useMemo(() => (!disabled && typeof error === 'string' ? error : ''), [error, disabled]);
  const currentState = useMemo(() => (currentError ? 'error' : 'default'), [currentError]);

  const {
    activeItem,
    buffer,
    close,
    displayValue,
    filterIsProcessing,
    getLabel,
    isActive,
    isOpen,
    localOptions,
    onKeyDown,
    onLocalType,
    selectItem,
    position,
    toggleIsOpen,
  } = useSelect({ ...useSelectProps, inputRef, optionsContentRef, disabled });

  const getOptionLabel = (item: Option) => {
    const localLabel = getLabel(item);
    if (!buffer) return localLabel;

    const search = makeSelectSearch(buffer);
    return HTMLParser(replaceSelectSearch(localLabel, search));
  };

  const optionsComponents = useMemo(
    () =>
      localOptions.map((item, index) => (
        <OptionItem key={uuid()} active={isActive(index)} onMouseUp={() => selectItem(index)}>
          {getOptionLabel(item)}
        </OptionItem>
      )),
    [localOptions, activeItem, buffer],
  );

  const noContentText = useMemo(
    () => (typeof noContent === 'string' ? noContent : noContent(buffer, filterIsProcessing, defaultNoContentMsg)),
    [noContent, buffer, filterIsProcessing, defaultNoContentMsg],
  );

  const hasOptions = useMemo(() => !!localOptions.length, [localOptions]);
  const showDropdown = useMemo(() => hasOptions || !!noContentText, [hasOptions, noContentText]);

  const onFocusHandler: FocusEventHandler<HTMLFuelTextFieldElement> = (event) => {
    if (onFocus) onFocus(event as unknown as CustomEvent);

    if (inputAutoFocus && onFocus) setTimeout(toggleIsOpen, 50);
    else if (inputAutoFocus) toggleIsOpen();
  };

  return (
    <Container>
      <FuelTextField
        controlled
        disabled={disabled}
        hasSuffix={!noArrow}
        hint={currentError}
        label={label}
        name={name}
        onClick={inputAutoFocus ? null : toggleIsOpen}
        onFocus={onFocusHandler}
        onFuel-input={onLocalType}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        readonly={!isOpen}
        ref={inputRef}
        state={currentState}
        value={displayValue}
      >
        {!noArrow && <Arrow open={isOpen} onClick={toggleIsOpen} slot="suffix" name="chevron-down" size="large" />}
      </FuelTextField>
      {showDropdown && (
        <SelectPortal>
          {isOpen && <OptionsWrapperCloser open={isOpen} onClick={close} />}
          <OptionsWrapper open={isOpen} {...position}>
            {isOpen && (
              <OptionsContent ref={optionsContentRef} scrollable={scrollable && hasOptions}>
                {hasOptions ? optionsComponents : <OptionItem clickable={false}>{noContentText}</OptionItem>}
              </OptionsContent>
            )}
          </OptionsWrapper>
        </SelectPortal>
      )}
    </Container>
  );
};
